import type { ConversationOpenSourceName } from 'shared-code/app-events'
import { DWELL_MIN_MS } from '../../hooks/use-visibility'

export interface ConversationDwellStretch {
  source: ConversationOpenSourceName
  containerId: string
  dwellMs: number
}

export interface ConversationDwellClock {
  /** The tab hid or the page is going (`pagehide`): the running stretch reports. */
  hidden: () => void
  /** The tab became visible again: a new stretch starts if none runs. */
  visible: () => void
  /** The panel closed (or the host unmounted): the running stretch reports; the clock is spent. */
  end: () => void
}

/**
 * The clock of ONE open of the conversation panel — framework-free so it is testable without a
 * DOM (`useTrackConversationDwell` wires it to React and the page). `source` / `containerId`
 * are captured at the open, so a flush on unmount during client navigation reports the old
 * container. Flushing clears the start before emitting: a close racing a `pagehide` (or a
 * `visibilitychange` → hidden followed by `pagehide`) reports once. A stretch under
 * `DWELL_MIN_MS` is a glance, not attention — dropped, as in `useVisibility`.
 */
export function createConversationDwellClock(opts: {
  source: ConversationOpenSourceName
  containerId: string
  /** Whether the tab is visible at the open — a background-tab deep link starts no stretch. */
  visibleAtOpen: boolean
  now: () => number
  emit: (stretch: ConversationDwellStretch) => void
}): ConversationDwellClock {
  const { source, containerId, now, emit } = opts
  let since: number | null = opts.visibleAtOpen ? now() : null
  let ended = false

  const flush = (): void => {
    if (since === null) {
      return
    }
    const dwellMs = Math.round(now() - since)
    since = null
    if (dwellMs >= DWELL_MIN_MS) {
      emit({ source, containerId, dwellMs })
    }
  }

  return {
    hidden: flush,
    visible: () => {
      if (!ended && since === null) {
        since = now()
      }
    },
    end: () => {
      flush()
      ended = true
    },
  }
}
